import { Fragment, useMemo, useState, useEffect } from 'react'
import { useBranch } from '../contexts/BranchContext'
import { washService } from '../services/firebase'
import notificationService from '../services/notificationService'
import {
  Clock,
  Car,
  Droplets,
  Wind,
  CheckCircle,
  Sparkles,
  Paintbrush,
  Info,
  User,
  Phone,
  CreditCard,
  ShoppingCart,
  X,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

const statusConfig = {
  waiting: {
    label: 'Waiting',
    icon: Clock,
    borderAccent: 'border-l-amber-500',
    badgeClass: 'bg-amber-500/15 text-amber-900 dark:text-amber-100 border-amber-500/30',
  },
  washing: {
    label: 'Washing',
    icon: Droplets,
    borderAccent: 'border-l-sky-500',
    badgeClass: 'bg-sky-500/15 text-sky-900 dark:text-sky-100 border-sky-500/30',
  },
  drying: {
    label: 'Drying',
    icon: Wind,
    borderAccent: 'border-l-violet-500',
    badgeClass: 'bg-violet-500/15 text-violet-900 dark:text-violet-100 border-violet-500/30',
  },
  polishing: {
    label: 'Polishing',
    icon: Sparkles,
    borderAccent: 'border-l-fuchsia-500',
    badgeClass: 'bg-fuchsia-500/15 text-fuchsia-900 dark:text-fuchsia-100 border-fuchsia-500/30',
  },
  interior: {
    label: 'Interior',
    icon: Paintbrush,
    borderAccent: 'border-l-cyan-500',
    badgeClass: 'bg-cyan-500/15 text-cyan-900 dark:text-cyan-100 border-cyan-500/30',
  },
  ready: {
    label: 'Ready',
    icon: CheckCircle,
    borderAccent: 'border-l-emerald-500',
    badgeClass: 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100 border-emerald-500/30',
  },
  picked_up: {
    label: 'Picked up',
    icon: CheckCircle,
    borderAccent: 'border-l-emerald-700',
    badgeClass: 'bg-emerald-700/15 text-emerald-950 dark:text-emerald-100 border-emerald-700/30',
  },
}

const statusOrder = ['waiting', 'washing', 'drying', 'polishing', 'interior', 'ready']

export default function WashQueuePage() {
  const { currentBranch, isViewingAllBranches, getActualBranches, getEffectiveBranchId } = useBranch()
  const [washes, setWashes] = useState([])
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState({})
  const [globalSearch, setGlobalSearch] = useState('')
  const [detailsWash, setDetailsWash] = useState(null)

  useEffect(() => {
    const branchId = getEffectiveBranchId()
    if (!branchId) return

    setLoading(true)

    if (isViewingAllBranches()) {
      async function loadAllBranchesData() {
        try {
          const branches = getActualBranches()
          let allWashes = []

          for (const branch of branches) {
            const branchWashes = await washService.getWashesByBranch(branch.id)
            const washesWithBranch = branchWashes.map((wash) => ({
              ...wash,
              branchName: branch.name,
            }))
            allWashes = [...allWashes, ...washesWithBranch]
          }

          const activeWashes = allWashes.filter((wash) => statusOrder.includes(wash.status))

          setWashes(activeWashes)
        } catch (error) {
          console.error('Error loading all branches data:', error)
        } finally {
          setLoading(false)
        }
      }

      loadAllBranchesData()
    } else {
      const unsubscribe = washService.listenToWashQueue(branchId, (updatedWashes) => {
        setWashes(updatedWashes)
        setLoading(false)
      })

      return unsubscribe
    }
  }, [currentBranch, isViewingAllBranches, getEffectiveBranchId, getActualBranches])

  async function updateWashStatus(washId, newStatus) {
    setUpdatingStatus((prev) => ({ ...prev, [washId]: true }))

    try {
      // Optimistic UI update (so the card moves immediately)
      setWashes((prev) => {
        if (newStatus === 'picked_up' || newStatus === 'cancelled') {
          return prev.filter((w) => w.id !== washId)
        }
        return prev.map((w) => (w.id === washId ? { ...w, status: newStatus } : w))
      })

      await washService.updateWashStatus(washId, newStatus)
      toast.success(`Status: ${statusConfig[newStatus]?.label || newStatus}`)

      if (newStatus === 'ready') {
        const wash = washes.find((w) => w.id === washId)
        if (wash && wash.customerId && wash.customerName && wash.carDetails) {
          try {
            await notificationService.sendCarReadyNotification(
              washId,
              wash.customerId,
              wash.customerName,
              wash.carDetails
            )
            toast.success('Customer notified. Vehicle ready.')
          } catch (error) {
            console.error('Failed to send notification:', error)
            toast.error('Status saved; notification failed.')
          }
        }
      }
    } catch (error) {
      console.error('Error updating wash status:', error)
      // Revert optimistic update on failure
      if (currentBranch) {
        try {
          if (isViewingAllBranches()) {
            const branches = getActualBranches()
            let allWashes = []
            for (const branch of branches) {
              const branchWashes = await washService.getWashesByBranch(branch.id)
              const washesWithBranch = branchWashes.map((wash) => ({
                ...wash,
                branchName: branch.name,
              }))
              allWashes = [...allWashes, ...washesWithBranch]
            }
            const activeWashes = allWashes.filter((wash) => statusOrder.includes(wash.status))
            setWashes(activeWashes)
          } else {
            const branchId = getEffectiveBranchId()
            const latest = await washService.getWashesByBranch(branchId)
            setWashes(latest.filter((wash) => statusOrder.includes(wash.status)))
          }
        } catch (e) {
          console.error('Failed to refresh after status error:', e)
        }
      }
      toast.error('Failed to update status')
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [washId]: false }))
    }
  }

  async function removeWaitingWash(wash) {
    if (!wash?.id) return
    if (wash.status !== 'waiting') {
      toast.error('Only waiting vehicles can be removed from the queue')
      return
    }
    const plate = wash?.carDetails?.licensePlate || wash?.customerName || 'this vehicle'
    const ok = window.confirm(
      `Remove ${plate} from the wash queue?\n\nUse this if the customer left before washing started.`
    )
    if (!ok) return

    setUpdatingStatus((prev) => ({ ...prev, [wash.id]: true }))
    try {
      setWashes((prev) => prev.filter((w) => w.id !== wash.id))
      await washService.updateWashStatus(wash.id, 'cancelled', {
        cancelledReason: 'customer_left',
      })
      toast.success('Removed from queue')
      if (detailsWash?.id === wash.id) setDetailsWash(null)
    } catch (error) {
      console.error('Error removing wash from queue:', error)
      setWashes((prev) => (prev.some((w) => w.id === wash.id) ? prev : [wash, ...prev]))
      toast.error('Could not remove from queue')
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [wash.id]: false }))
    }
  }

  function formatTime(timestamp) {
    if (!timestamp) return ''
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const washesByStatus = statusOrder.reduce((acc, status) => {
    acc[status] = washes.filter((wash) => wash.status === status)
    return acc
  }, {})

  const filteredWashesByStatus = useMemo(() => {
    const out = {}
    const q = globalSearch.trim().toLowerCase()
    for (const status of statusOrder) {
      if (!q) {
        out[status] = washesByStatus[status]
        continue
      }

      out[status] = washesByStatus[status].filter((wash) => {
        const plate = wash?.carDetails?.licensePlate || ''
        const make = wash?.carDetails?.make || ''
        const model = wash?.carDetails?.model || ''
        const color = wash?.carDetails?.color || ''
        const service = wash?.serviceType || ''
        const name = wash?.customerName || ''
        const phone = wash?.customerPhone || ''
        const branch = wash?.branchName || ''
        return [plate, make, model, color, service, name, phone, branch].some((v) =>
          String(v).toLowerCase().includes(q)
        )
      })
    }
    return out
  }, [globalSearch, washesByStatus])

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-6">
      <div className="border-b border-border pb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Operations
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Wash queue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isViewingAllBranches()
            ? 'All branches. Live pipeline.'
            : `${currentBranch?.name || 'Branch'}. Move vehicles through each stage.`}
        </p>
        <div className="mt-4 max-w-xl">
          <Input
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            placeholder="Search whole queue: reg / customer / service / make / model..."
            className="h-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {statusOrder.map((status) => {
          const config = statusConfig[status]
          const count = washesByStatus[status].length
          return (
            <Card key={status} className="shadow-sm border-border/80">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-primary">
                  <config.icon className="size-5" />
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums leading-none">{count}</p>
                  <p className="text-xs font-medium text-muted-foreground mt-1">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
        {statusOrder.map((status) => {
          const config = statusConfig[status]
          const statusWashes = filteredWashesByStatus[status]

          return (
            <div key={status} className="space-y-3">
              <div className="flex items-center gap-2 px-0.5">
                <config.icon className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">
                  {config.label}{' '}
                  <span className="text-muted-foreground font-normal">({statusWashes.length})</span>
                </h2>
              </div>

              <div className="space-y-3">
                {statusWashes.length === 0 ? (
                  <Card className="border-dashed bg-muted/20 shadow-none">
                    <CardContent className="py-10 text-center text-sm text-muted-foreground">
                      None in {config.label.toLowerCase()}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="max-h-[62vh] space-y-2 overflow-y-auto pr-1">
                    {statusWashes.map((wash) => (
                      <WashCard
                        key={wash.id}
                        wash={wash}
                        config={config}
                        isUpdating={updatingStatus[wash.id]}
                        formatTime={formatTime}
                        showBranch={isViewingAllBranches()}
                        onOpenDetails={() => setDetailsWash(wash)}
                        onRemoveFromQueue={
                          wash.status === 'waiting' ? () => removeWaitingWash(wash) : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <WashDetailsDialog
        open={!!detailsWash}
        wash={detailsWash}
        onClose={() => setDetailsWash(null)}
        onStatusUpdate={updateWashStatus}
        onRemoveFromQueue={removeWaitingWash}
        isUpdating={detailsWash ? !!updatingStatus[detailsWash.id] : false}
      />
    </div>
  )
}

function WashCard({
  wash,
  config,
  isUpdating,
  formatTime,
  showBranch,
  onOpenDetails,
  onRemoveFromQueue,
}) {
  const isPaid = wash.paymentStatus === 'paid' || wash.paymentStatus === 'on_account'
  const paidLabel =
    wash.paymentStatus === 'on_account' ? 'On account' : isPaid ? 'Paid' : 'Unpaid'
  const carSummary = wash?.carDetails
    ? `${wash.carDetails.make || ''} ${wash.carDetails.model || ''}`.trim() || 'Vehicle'
    : 'Vehicle'
  const color = wash?.carDetails?.color || ''
  const plate = wash?.carDetails?.licensePlate || ''
  const service = wash?.serviceType || 'Service'

  return (
    <Card
      className={cn(
        'overflow-hidden border-l-4 shadow-sm',
        config.borderAccent
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <Car className="size-4 shrink-0 text-muted-foreground" />
              <p className="truncate text-sm font-semibold text-foreground">
                {carSummary}
                {color ? ` · ${color}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-6 text-xs text-muted-foreground">
              <span className="font-medium text-foreground/90">{plate || 'No reg'}</span>
              <span className="text-muted-foreground">|</span>
              <span className="truncate">{service}</span>
              <span className="text-muted-foreground">|</span>
              <span
                className={cn(
                  'font-semibold',
                  isPaid ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'
                )}
              >
                {paidLabel}
              </span>
              {showBranch && wash.branchName && (
                <>
                  <span className="text-muted-foreground">|</span>
                  <span className="truncate">{wash.branchName}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {formatTime(wash.createdAt)}
            </span>
            {onRemoveFromQueue && (
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onRemoveFromQueue}
                disabled={isUpdating}
                title="Remove from queue"
              >
                <X className="size-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onOpenDetails}
              disabled={isUpdating}
              title="Details"
            >
              <Info className="size-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function WashDetailsDialog({ open, wash, onClose, onStatusUpdate, onRemoveFromQueue, isUpdating }) {
  const isPaid = wash?.paymentStatus === 'paid' || wash?.paymentStatus === 'on_account'
  const paidLabel =
    wash?.paymentStatus === 'on_account' ? 'On account' : isPaid ? 'Paid' : 'Unpaid'
  const canComplete =
    wash?.status === 'ready' && (wash?.paymentStatus === 'paid' || wash?.paymentStatus === 'on_account')
  const editableStatuses = Object.keys(statusConfig).filter((s) => s !== 'picked_up')
  const [actionStatus, setActionStatus] = useState(wash?.status || 'waiting')

  useEffect(() => {
    if (wash?.status) setActionStatus(wash.status)
  }, [wash?.status])

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="mx-auto flex min-h-full max-w-lg items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <Dialog.Panel className="w-full overflow-hidden rounded-2xl border border-border bg-background shadow-xl">
                <div className="border-b border-border p-4">
                  <Dialog.Title className="text-base font-semibold text-foreground">
                    Wash details
                  </Dialog.Title>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Update status, view vehicle/customer details, and open POS (if unpaid).
                  </p>
                </div>

                <div className="space-y-4 p-4">
                  <div className="grid grid-cols-1 gap-3 text-sm">
                    <div className="rounded-xl border border-border bg-muted/20 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Vehicle</p>
                      <p className="mt-1 font-semibold text-foreground">
                        {wash?.carDetails?.make || '—'} {wash?.carDetails?.model || ''}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {wash?.carDetails?.color || '—'}
                        {wash?.carDetails?.licensePlate ? ` · ${wash.carDetails.licensePlate}` : ''}
                      </p>
                    </div>

                    <div className="rounded-xl border border-border bg-muted/20 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Service</p>
                      <p className="mt-1 font-semibold text-foreground">{wash?.serviceType || '—'}</p>
                      <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Amount</span>
                        <span className="font-semibold text-foreground tabular-nums">
                          R{wash?.amount ?? 0}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Payment</span>
                        <span
                          className={cn(
                            'font-semibold',
                            isPaid ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'
                          )}
                        >
                          {paidLabel}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-muted/20 p-3">
                      <p className="text-xs font-medium text-muted-foreground">Customer</p>
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="size-4 text-muted-foreground" />
                          <span className="font-semibold text-foreground">
                            {wash?.customerName || 'Walk-in customer'}
                          </span>
                        </div>
                        {wash?.customerPhone && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Phone className="size-4" />
                            <span>{wash.customerPhone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border p-3">
                    <p className="text-xs font-medium text-muted-foreground">Change wash status</p>
                    <div className="mt-2 flex gap-2">
                      <select
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        value={actionStatus}
                        onChange={(e) => setActionStatus(e.target.value)}
                        disabled={isUpdating}
                      >
                        {editableStatuses.map((s) => (
                          <option key={s} value={s}>
                            {statusConfig[s].label}
                          </option>
                        ))}
                      </select>
                      <Button
                        disabled={isUpdating || !wash || actionStatus === wash.status}
                        onClick={() => wash && onStatusUpdate(wash.id, actionStatus)}
                      >
                        Save
                      </Button>
                    </div>
                    {wash?.status === 'waiting' && (
                      <Button
                        className="mt-2 w-full"
                        variant="destructive"
                        disabled={isUpdating || !wash}
                        onClick={() => wash && onRemoveFromQueue?.(wash)}
                      >
                        <X className="size-4" />
                        Remove from queue (customer left)
                      </Button>
                    )}
                    <div className="mt-2 flex gap-2">
                      <Button
                        className="w-full"
                        variant="secondary"
                        disabled={isUpdating || !canComplete || !wash}
                        onClick={() => wash && onStatusUpdate(wash.id, 'picked_up')}
                        title={!canComplete ? 'Can only complete when status is Ready and payment is settled (paid or on account)' : undefined}
                      >
                        Customer picked up (Complete)
                      </Button>
                      {wash?.paymentStatus === 'unpaid' || !wash?.paymentStatus ? (
                        <Link
                          to={wash ? `/pos/${wash.id}` : '#'}
                          className={cn(buttonVariants({ variant: 'outline' }), 'w-full inline-flex gap-2')}
                          onClick={onClose}
                        >
                          <ShoppingCart className="size-4" />
                          Open POS
                        </Link>
                      ) : (
                        <Button className="w-full" variant="outline" disabled>
                          {wash?.paymentStatus === 'on_account' ? 'On account (POS locked)' : 'Paid (POS locked)'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-border p-3 flex justify-end">
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
